import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import type { FC } from 'react';

import { Button, Checkbox, Icon, Table } from '@wf/components';

import { selectedLicenseTypeTransitionsSelector } from '../../../License/store/selectors';
import EmptyTableContent from '../../../../components/EmptyTableContent';
import LicenseStatus from '../../../../components/LicenseStatus';
import DateTimeCell from '../../../../components/DateTimeCell';
import { OperationType } from '../../../License/types/enums';
import TableFooter from '../../../../components/TableFooter';
import Loader from '../../../../components/Loader';

import useOperationName from '../../../../hooks/useOperationName';

import { documentHistorySelector } from '../../../../store/audit/selectors';

import styles from './DocumentHistoryTable.module.scss';

const getTableHeaders = (t: (key: string) => string) => [
  { name: t('Operation Date'), flex: 2 },
  { name: t('User'), flex: 2 },
  { name: t('Committed Operations'), flex: 2 },
  { name: t('New Status'), flex: 2 },
  { name: '', flex: 1 },
];

const DocumentHistoryTable: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams();

  const tableHeaders = useMemo(() => getTableHeaders(t), [t]);

  const { data: documentHistory, isLoading } = useSelector(documentHistorySelector);

  const selectedLicenseTypeTransitions = useSelector(selectedLicenseTypeTransitionsSelector);

  const getOperationName = useOperationName();

  const [checkedItems, setCheckedItems] = useState<string[]>([]);

  const handleCheck = (checked: boolean, modifiedOnEpoch: string): void => {
    if (checked) {
      setCheckedItems([...checkedItems, modifiedOnEpoch].sort());
    } else {
      setCheckedItems(checkedItems.filter((item) => item !== modifiedOnEpoch));
    }
  };

  const handleCompareButtonClick = () => {
    const [leftEpochMillis, rightEpochMillis] = checkedItems as [string, string];
    const query = new URLSearchParams({ leftEpochMillis, rightEpochMillis }).toString();

    navigate(`../../versionComparison/${id}?${query}`, { relative: 'path' });
  };

  const isComparisonEnabled = documentHistory && documentHistory.length > 1;

  return (
    <div className={styles.tableContainer}>
      <Table>
        {isComparisonEnabled && (
          <Table.Search className={styles.actions}>
            <Button
              leftIcon={<Icon name="ic_compare_arrows" />}
              onClick={handleCompareButtonClick}
              disabled={checkedItems.length < 2}
              color="typography"
              secondary
            >
              {t('Compare')}
            </Button>
          </Table.Search>
        )}
        <Table.Grid className="relative">
          <Table.Header>
            <Table.Row>
              {isComparisonEnabled && <Table.HeaderCell className={styles.checkboxCell} />}
              {tableHeaders.map(({ name, flex }) => (
                <Table.HeaderCell
                  style={{
                    flex,
                  }}
                  key={name}
                >
                  {name}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body maxRows={12}>
            {documentHistory.map(
              ({ modifiedOn, modifiedBy, modifiedOnEpoch, operation, supportInfoMessage, status }, index) => {
                const { operationStatus = null, operationStatusInNationalLang = null } =
                  selectedLicenseTypeTransitions[
                    operation !== OperationType.RETURN ? operation : documentHistory[index + 2]?.operation ?? ''
                  ] || {};

                const checked = checkedItems.includes(modifiedOnEpoch);

                return (
                  <Table.Expandable key={modifiedOn}>
                    <Table.Row>
                      {isComparisonEnabled && (
                        <Table.Cell className={styles.checkboxCell}>
                          <Checkbox
                            onChange={(checked) => handleCheck(checked, modifiedOnEpoch)}
                            disabled={!checked && checkedItems.length === 2}
                            checked={checked}
                          />
                        </Table.Cell>
                      )}
                      <Table.Cell className={styles.bigCell}>
                        <DateTimeCell date={modifiedOn} />
                      </Table.Cell>
                      <Table.Cell className={styles.bigCell}>{modifiedBy}</Table.Cell>
                      <Table.Cell className={styles.bigCell}>{getOperationName(operation)}</Table.Cell>
                      <Table.Cell className={styles.bigCell}>
                        <LicenseStatus
                          ogaStatusInNationalLang={operationStatusInNationalLang}
                          ogaStatus={operationStatus}
                          status={status}
                        />
                      </Table.Cell>
                      <Table.Cell
                        className={classNames(styles.lastCell)}
                        expandOnClick={!!supportInfoMessage}
                        align="right"
                        withoutEllipsis
                      >
                        {supportInfoMessage && <Icon.UnreadCommentIcon size={18} />}
                      </Table.Cell>
                    </Table.Row>
                    {supportInfoMessage && (
                      <Table.Expandable.Content>
                        <div className={styles.supportInfoMessage}>{supportInfoMessage}</div>
                      </Table.Expandable.Content>
                    )}
                  </Table.Expandable>
                );
              }
            )}
          </Table.Body>
          {isLoading && <Loader className={styles.loader} />}
          {!documentHistory.length && !isLoading && (
            <EmptyTableContent iconName="ministry" title={t('No document history data yet')} />
          )}
          <TableFooter page={1} totalCount={documentHistory.length} withPagination={false} />
        </Table.Grid>
      </Table>
    </div>
  );
};

export default DocumentHistoryTable;
